
import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { updateSEO } from '../../utils/seo';

export default function SubmitAgency() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Basic Information
    agencyName: '',
    website: '',
    foundedYear: '',
    location: '',
    description: '',
    
    // Business Details
    category: '',
    industry: '',
    businessModel: '',
    teamSize: '',
    
    // Financial Information
    monthlyRevenue: '',
    annualRevenue: '',
    profitMargin: '',
    askingPrice: '',
    
    // Technical Details
    techStack: '',
    clientBase: '',
    keyServices: '',
    
    // Contact Information
    ownerName: '',
    ownerEmail: '',
    ownerPhone: '',
    preferredContact: ''
  });

  const totalSteps = 4;

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || window.location.origin;
    
    const seoData = {
      title: 'Sell Your AI Agency - List Your Business on AIExchange',
      description: 'List your AI agency or business for sale on our exclusive marketplace. Connect with serious buyers and get your AI company in front of investors and entrepreneurs.',
      keywords: 'sell AI agency, list AI business, AI business for sale, sell AI company, AI agency marketplace, business listing',
      ogTitle: 'Sell Your AI Agency - List Your Business on AIExchange',
      ogDescription: 'List your AI agency or business for sale on our exclusive marketplace. Connect with serious buyers and get your AI company in front of investors and entrepreneurs.',
      ogImage: 'https://public.readdy.ai/ai/img_res/2d70e80287476cd1d37f0d783ccd08a9.jpg',
      twitterCard: 'summary_large_image',
      canonicalUrl: `${siteUrl}/submit`,
      structuredData: {
        "@context": "https://schema.org",
        "@type": "WebPage",
        "@id": `${siteUrl}/submit`,
        "name": "Submit Your AI Agency",
        "description": "List your AI agency or business for sale on our marketplace",
        "url": `${siteUrl}/submit`,
        "mainEntity": {
          "@type": "Service",
          "name": "AI Business Listing Service",
          "description": "Professional listing service for AI agencies and businesses",
          "provider": {
            "@type": "Organization",
            "name": "AIExchange"
          }
        }
      }
    };

    updateSEO(seoData);
  }, []);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const submitData = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      submitData.append(key, value);
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/submit/agency-submission', {
        method: 'POST',
        body: new URLSearchParams(submitData as any)
      });

      if (response.ok) {
        alert('Your agency submission has been received! We\'ll review it within 24 hours.');
        // Reset form
        setFormData({
          agencyName: '', website: '', foundedYear: '', location: '', description: '',
          category: '', industry: '', businessModel: '', teamSize: '',
          monthlyRevenue: '', annualRevenue: '', profitMargin: '', askingPrice: '',
          techStack: '', clientBase: '', keyServices: '',
          ownerName: '', ownerEmail: '', ownerPhone: '', preferredContact: ''
        });
        setCurrentStep(1);
      }
    } catch (error) {
      alert('There was an error submitting your form. Please try again.');
    }
  };

  const nextStep = () => {
    if (currentStep < totalSteps) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-black to-gray-900" />
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }} />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-8">
            <div className="w-2 h-2 bg-blue-400 rounded-full mr-3 animate-pulse" />
            <span className="text-sm font-medium text-gray-300">List Your AI Agency</span>
          </div>
          
          <h1 className="text-5xl md:text-7xl font-light text-white mb-6 leading-tight tracking-tight">
            Sell Your
            <br />
            <span className="font-semibold bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
              AI Agency
            </span>
          </h1>
          
          <p className="text-xl text-gray-400 max-w-2xl mx-auto font-light leading-relaxed">
            Connect with serious buyers and get your AI agency in front of investors, entrepreneurs, and strategic acquirers.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          {/* Progress Bar */}
          <div className="mb-12">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-gray-400">Step {currentStep} of {totalSteps}</span>
              <span className="text-sm font-medium text-gray-400">{Math.round((currentStep / totalSteps) * 100)}% Complete</span>
            </div>
            <div className="w-full bg-white/5 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-blue-400 to-cyan-400 h-2 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${(currentStep / totalSteps) * 100}%` }}
              />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8" data-readdy-form id="agency-submission">
            {/* Step 1: Basic Information */}
            {currentStep === 1 && (
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8">
                <h2 className="text-3xl font-light text-white mb-8">Basic Information</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">Agency Name *</label>
                    <input
                      type="text"
                      name="agencyName"
                      value={formData.agencyName}
                      onChange={(e) => handleInputChange('agencyName', e.target.value)}
                      className="w-full px-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-transparent transition-all duration-200"
                      placeholder="Enter your agency name"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">Website URL *</label>
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={(e) => handleInputChange('website', e.target.value)}
                      className="w-full px-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-transparent transition-all duration-200"
                      placeholder="https://youragency.com"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">Founded Year *</label>
                    <input
                      type="number"
                      name="foundedYear"
                      value={formData.foundedYear}
                      onChange={(e) => handleInputChange('foundedYear', e.target.value)}
                      className="w-full px-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-transparent transition-all duration-200"
                      placeholder="2020"
                      min="2000"
                      max="2024"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">Location *</label>
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      className="w-full px-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-transparent transition-all duration-200"
                      placeholder="San Francisco, CA"
                      required
                    />
                  </div>
                </div>
                
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-300 mb-3">Agency Description *</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-transparent transition-all duration-200 resize-none"
                    placeholder="Describe your AI agency, services, and unique value proposition..."
                    required
                  />
                  <div className="text-right text-sm text-gray-500 mt-2">
                    {formData.description.length}/500 characters
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Business Details */}
            {currentStep === 2 && (
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8">
                <h2 className="text-3xl font-light text-white mb-8">Business Details</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">Category *</label>
                    <div className="relative">
                      <select
                        name="category"
                        value={formData.category}
                        onChange={(e) => handleInputChange('category', e.target.value)}
                        className="w-full px-4 py-4 pr-8 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-transparent transition-all duration-200 appearance-none"
                        required
                      >
                        <option value="">Select category</option>
                        <option value="AI Agency">AI Agency</option>
                        <option value="AIaaS">AIaaS</option>
                        <option value="Automation Platform">Automation Platform</option>
                        <option value="AI Infrastructure">AI Infrastructure</option>
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">Primary Industry *</label>
                    <div className="relative">
                      <select
                        name="industry"
                        value={formData.industry}
                        onChange={(e) => handleInputChange('industry', e.target.value)}
                        className="w-full px-4 py-4 pr-8 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-transparent transition-all duration-200 appearance-none"
                        required
                      >
                        <option value="">Select industry</option>
                        <option value="Marketing">Marketing</option>
                        <option value="Healthcare">Healthcare</option>
                        <option value="Real Estate">Real Estate</option>
                        <option value="Legal">Legal</option>
                        <option value="E-Commerce">E-Commerce</option>
                        <option value="Finance">Finance</option>
                        <option value="Other">Other</option>
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">Business Model *</label>
                    <div className="relative">
                      <select
                        name="businessModel"
                        value={formData.businessModel}
                        onChange={(e) => handleInputChange('businessModel', e.target.value)}
                        className="w-full px-4 py-4 pr-8 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-transparent transition-all duration-200 appearance-none"
                        required
                      >
                        <option value="">Select model</option>
                        <option value="Service-based">Service-based</option>
                        <option value="SaaS">SaaS</option>
                        <option value="Hybrid">Hybrid</option>
                        <option value="Consulting">Consulting</option>
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">Team Size *</label>
                    <div className="relative">
                      <select
                        name="teamSize"
                        value={formData.teamSize}
                        onChange={(e) => handleInputChange('teamSize', e.target.value)}
                        className="w-full px-4 py-4 pr-8 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-transparent transition-all duration-200 appearance-none"
                        required
                      >
                        <option value="">Select team size</option>
                        <option value="1-5">1-5 employees</option>
                        <option value="6-15">6-15 employees</option>
                        <option value="16-50">16-50 employees</option>
                        <option value="50+">50+ employees</option>
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">Key Services</label>
                    <textarea
                      name="keyServices"
                      value={formData.keyServices}
                      onChange={(e) => handleInputChange('keyServices', e.target.value)}
                      rows={3}
                      maxLength={300}
                      className="w-full px-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-transparent transition-all duration-200 resize-none"
                      placeholder="List your main AI services and offerings..."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">Client Base</label>
                    <textarea
                      name="clientBase"
                      value={formData.clientBase}
                      onChange={(e) => handleInputChange('clientBase', e.target.value)}
                      rows={3}
                      maxLength={300}
                      className="w-full px-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-transparent transition-all duration-200 resize-none"
                      placeholder="Describe your typical clients and market..."
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Financial Information */}
            {currentStep === 3 && (
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8">
                <h2 className="text-3xl font-light text-white mb-8">Financial Information</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">Monthly Recurring Revenue *</label>
                    <div className="relative">
                      <select
                        name="monthlyRevenue"
                        value={formData.monthlyRevenue}
                        onChange={(e) => handleInputChange('monthlyRevenue', e.target.value)}
                        className="w-full px-4 py-4 pr-8 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-transparent transition-all duration-200 appearance-none"
                        required
                      >
                        <option value="">Select MRR range</option>
                        <option value="$0-$5K">$0 - $5K</option>
                        <option value="$5K-$25K">$5K - $25K</option>
                        <option value="$25K-$100K">$25K - $100K</option>
                        <option value="$100K-$500K">$100K - $500K</option>
                        <option value="$500K+">$500K+</option>
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">Annual Revenue *</label>
                    <div className="relative">
                      <select
                        name="annualRevenue"
                        value={formData.annualRevenue}
                        onChange={(e) => handleInputChange('annualRevenue', e.target.value)}
                        className="w-full px-4 py-4 pr-8 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-transparent transition-all duration-200 appearance-none"
                        required
                      >
                        <option value="">Select annual revenue</option>
                        <option value="$0-$100K">$0 - $100K</option>
                        <option value="$100K-$500K">$100K - $500K</option>
                        <option value="$500K-$1M">$500K - $1M</option>
                        <option value="$1M-$5M">$1M - $5M</option>
                        <option value="$5M+">$5M+</option>
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">Profit Margin *</label>
                    <div className="relative">
                      <select
                        name="profitMargin"
                        value={formData.profitMargin}
                        onChange={(e) => handleInputChange('profitMargin', e.target.value)}
                        className="w-full px-4 py-4 pr-8 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-transparent transition-all duration-200 appearance-none"
                        required
                      >
                        <option value="">Select profit margin</option>
                        <option value="0-20%">0-20%</option>
                        <option value="20-40%">20-40%</option>
                        <option value="40-60%">40-60%</option>
                        <option value="60%+">60%+</option>
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">Asking Price *</label>
                    <div className="relative">
                      <select
                        name="askingPrice"
                        value={formData.askingPrice}
                        onChange={(e) => handleInputChange('askingPrice', e.target.value)}
                        className="w-full px-4 py-4 pr-8 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-transparent transition-all duration-200 appearance-none"
                        required
                      >
                        <option value="">Select asking price</option>
                        <option value="$50K-$250K">$50K - $250K</option>
                        <option value="$250K-$1M">$250K - $1M</option>
                        <option value="$1M-$5M">$1M - $5M</option>
                        <option value="$5M-$25M">$5M - $25M</option>
                        <option value="$25M+">$25M+</option>
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-300 mb-3">Technology Stack</label>
                  <textarea
                    name="techStack"
                    value={formData.techStack}
                    onChange={(e) => handleInputChange('techStack', e.target.value)}
                    rows={3}
                    maxLength={400}
                    className="w-full px-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-transparent transition-all duration-200 resize-none"
                    placeholder="List your key technologies, AI models, platforms, and tools..."
                  />
                </div>
              </div>
            )}

            {/* Step 4: Contact Information */}
            {currentStep === 4 && (
              <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8">
                <h2 className="text-3xl font-light text-white mb-8">Contact Information</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">Owner/Contact Name *</label>
                    <input
                      type="text"
                      name="ownerName"
                      value={formData.ownerName}
                      onChange={(e) => handleInputChange('ownerName', e.target.value)}
                      className="w-full px-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-transparent transition-all duration-200"
                      placeholder="Your full name"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">Email Address *</label>
                    <input
                      type="email"
                      name="ownerEmail"
                      value={formData.ownerEmail}
                      onChange={(e) => handleInputChange('ownerEmail', e.target.value)}
                      className="w-full px-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-transparent transition-all duration-200"
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">Phone Number</label>
                    <input
                      type="tel"
                      name="ownerPhone"
                      value={formData.ownerPhone}
                      onChange={(e) => handleInputChange('ownerPhone', e.target.value)}
                      className="w-full px-4 py-4 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-transparent transition-all duration-200"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-3">Preferred Contact Method *</label>
                    <div className="relative">
                      <select
                        name="preferredContact"
                        value={formData.preferredContact}
                        onChange={(e) => handleInputChange('preferredContact', e.target.value)}
                        className="w-full px-4 py-4 pr-8 bg-white/5 border border-white/10 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-blue-400/50 focus:border-transparent transition-all duration-200 appearance-none"
                        required
                      >
                        <option value="">Select preference</option>
                        <option value="Email">Email</option>
                        <option value="Phone">Phone</option>
                        <option value="Both">Both</option>
                      </select>
                      <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8 p-6 bg-blue-500/10 border border-blue-500/20 rounded-2xl">
                  <div className="flex items-start space-x-3">
                    <i className="ri-information-line text-blue-400 text-xl mt-1"></i>
                    <div>
                      <h3 className="text-white font-medium mb-2">What happens next?</h3>
                      <ul className="text-gray-300 text-sm space-y-1">
                        <li>• We'll review your submission within 24 hours</li>
                        <li>• Our team will verify your agency details</li>
                        <li>• You'll receive listing approval and next steps</li>
                        <li>• Your agency will go live on our marketplace</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center pt-8">
              <div>
                {currentStep > 1 && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={prevStep}
                    className="px-8 py-3"
                  >
                    <i className="ri-arrow-left-line mr-2"></i>
                    Previous
                  </Button>
                )}
              </div>
              
              <div>
                {currentStep < totalSteps ? (
                  <Button
                    type="button"
                    variant="primary"
                    onClick={nextStep}
                    className="px-8 py-3"
                  >
                    Continue
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Button>
                ) : (
                  <Button
                    type="submit"
                    variant="primary"
                    className="px-8 py-3"
                  >
                    Submit Agency
                    <i className="ri-send-plane-line ml-2"></i>
                  </Button>
                )}
              </div>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
